import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { queryClient, apiRequest } from "@/lib/queryClient";
import {
  Package, Plus, Search, AlertTriangle, CheckCircle2,
  ShoppingCart, TriangleAlert,
} from "lucide-react";
import { useGaContext } from "@/hooks/useGaContext";

export default function GALicensePool() {
  const { gaId, gaProfile } = useGaContext();
  const { toast } = useToast();

  // Existing dialogs
  const [showAssignDialog, setShowAssignDialog]   = useState(false);
  const [revokeTarget, setRevokeTarget]           = useState<any>(null);
  const [searchQuery, setSearchQuery]             = useState("");
  const [selectedBlock, setSelectedBlock]         = useState("");
  const [brokerEmail, setBrokerEmail]             = useState("");
  const [brokerName, setBrokerName]               = useState("");

  // Request licenses dialog
  const [showRequestDialog, setShowRequestDialog] = useState(false);
  const [requestDone, setRequestDone]             = useState(false);
  const [requestResult, setRequestResult]         = useState<any>(null);
  const [reqQuantity, setReqQuantity]             = useState(10);
  const [reqTimeline, setReqTimeline]             = useState("within_30_days");
  const [reqNotes, setReqNotes]                   = useState("");
  const [reqContactName, setReqContactName]       = useState((gaProfile as any)?.contactName || "");
  const [reqContactEmail, setReqContactEmail]     = useState((gaProfile as any)?.contactEmail || "");

  const { data: blocks = [], isLoading: blocksLoading } = useQuery<any[]>({
    queryKey: ["/api/ga", gaId, "license-blocks"],
    queryFn: async () => {
      const res = await fetch(`/api/ga/${gaId}/license-blocks`);
      if (!res.ok) return [];
      return res.json();
    },
    enabled: !!gaId,
  });

  const { data: brokerLinks = [] } = useQuery<any[]>({
    queryKey: ["/api/ga", gaId, "broker-links"],
    queryFn: async () => {
      const res = await fetch(`/api/ga/${gaId}/broker-links`);
      if (!res.ok) return [];
      return res.json();
    },
    enabled: !!gaId,
  });

  const assignMutation = useMutation({
    mutationFn: async (data: any) => apiRequest("POST", `/api/ga/${gaId}/broker-links`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/ga", gaId, "broker-links"] });
      queryClient.invalidateQueries({ queryKey: ["/api/ga", gaId, "license-blocks"] });
      setShowAssignDialog(false);
      setBrokerEmail(""); setBrokerName(""); setSelectedBlock("");
      toast({ title: "License assigned", description: "Broker invite sent successfully." });
    },
    onError: () => toast({ title: "Error", description: "Failed to assign license.", variant: "destructive" }),
  });

  const revokeMutation = useMutation({
    mutationFn: async (linkId: string) => apiRequest("DELETE", `/api/ga/${gaId}/broker-links/${linkId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/ga", gaId, "broker-links"] });
      queryClient.invalidateQueries({ queryKey: ["/api/ga", gaId, "license-blocks"] });
      setRevokeTarget(null);
      toast({ title: "License revoked", description: "The broker link has been revoked." });
    },
    onError: () => toast({ title: "Error", description: "Failed to revoke license.", variant: "destructive" }),
  });

  const requestLicensesMutation = useMutation({
    mutationFn: async (data: any) => {
      const res = await apiRequest("POST", `/api/ga/${gaId}/license-requests`, data);
      return res;
    },
    onSuccess: (data: any) => {
      setRequestResult(data);
      setRequestDone(true);
    },
    onError: () => toast({ title: "Error", description: "Failed to submit request. Please try again.", variant: "destructive" }),
  });

  const totalPurchased = blocks.reduce((sum: number, b: any) => sum + (b.totalLicenses || 0), 0);
  const totalAssigned  = brokerLinks.filter((l: any) => l.status !== "license_revoked").length;
  const totalAvailable = totalPurchased - totalAssigned;
  const lowOnLicenses  = totalPurchased > 0 && totalAvailable <= Math.max(2, Math.floor(totalPurchased * 0.15));

  const openRequestDialog = () => {
    setRequestDone(false);
    setRequestResult(null);
    setReqQuantity(10);
    setReqTimeline("within_30_days");
    setReqNotes("");
    setReqContactName((gaProfile as any)?.contactName || "");
    setReqContactEmail((gaProfile as any)?.contactEmail || "");
    setShowRequestDialog(true);
  };

  const TIMELINE_LABELS: Record<string, string> = {
    asap:           "As soon as possible",
    within_30_days: "Within 30 days",
    within_60_days: "Within 60 days",
    planning_ahead: "Planning ahead (60+ days)",
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">License Pool</h1>
          <p className="text-sm text-muted-foreground mt-0.5">Manage and assign broker license seats</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={openRequestDialog} className="gap-1.5">
            <ShoppingCart className="w-4 h-4" /> Request More Licenses
          </Button>
          <Button onClick={() => setShowAssignDialog(true)}>
            <Plus className="w-4 h-4 mr-2" /> Assign License
          </Button>
        </div>
      </div>

      {/* Low-license warning banner */}
      {lowOnLicenses && (
        <div className="flex items-start gap-3 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950 px-4 py-3">
          <TriangleAlert className="w-4 h-4 text-amber-500 dark:text-amber-400 shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-amber-800 dark:text-amber-300">
              Running low on available licenses — {totalAvailable} remaining
            </p>
            <p className="text-xs text-amber-600 dark:text-amber-400 mt-0.5">
              Request additional seats before you run out and can't onboard new broker agencies.
            </p>
          </div>
          <Button size="sm" variant="outline"
            className="shrink-0 h-7 text-xs border-amber-300 dark:border-amber-700 text-amber-700 dark:text-amber-400 hover:bg-amber-100 dark:hover:bg-amber-900"
            onClick={openRequestDialog}>
            Request Now
          </Button>
        </div>
      )}

      {/* Summary cards */}
      <div className="grid grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-5">
            <p className="text-xs text-muted-foreground uppercase tracking-wide">Total Purchased</p>
            <p className="text-3xl font-bold text-foreground mt-1">{totalPurchased}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-5">
            <p className="text-xs text-muted-foreground uppercase tracking-wide">Assigned</p>
            <p className="text-3xl font-bold text-primary mt-1">{totalAssigned}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-5">
            <p className="text-xs text-muted-foreground uppercase tracking-wide">Available</p>
            <p className={`text-3xl font-bold mt-1 ${lowOnLicenses ? "text-amber-500 dark:text-amber-400" : "text-green-600 dark:text-green-400"}`}>
              {Math.max(0, totalAvailable)}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* License Blocks */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-semibold text-muted-foreground">License Blocks</CardTitle>
            <Button size="sm" variant="ghost" className="h-7 text-xs text-primary hover:text-primary/80 gap-1"
              onClick={openRequestDialog}>
              <Plus className="w-3 h-3" /> Request additional seats
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {blocksLoading ? (
            <div className="flex items-center justify-center py-10">
              <div className="animate-spin w-5 h-5 border-2 border-primary border-t-transparent rounded-full" />
            </div>
          ) : blocks.length === 0 ? (
            <div className="text-center py-10 text-muted-foreground">
              <Package className="w-8 h-8 mx-auto mb-2 opacity-40" />
              <p className="text-sm">No license blocks purchased yet</p>
              <p className="text-xs mt-1 mb-4">Contact Administr to purchase license blocks for your GA</p>
              <Button size="sm" variant="outline" onClick={openRequestDialog} className="gap-1.5">
                <ShoppingCart className="w-3.5 h-3.5" /> Request License Seats
              </Button>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-xs text-muted-foreground border-b border-border">
                <tr>
                  <th className="text-left pb-2">Block ID</th>
                  <th className="text-left pb-2">Purchased</th>
                  <th className="text-left pb-2">Total Seats</th>
                  <th className="text-left pb-2">Billing Ref</th>
                  <th className="text-left pb-2">Notes</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {blocks.map((block: any) => (
                  <tr key={block.id}>
                    <td className="py-2.5 font-mono text-xs text-muted-foreground">{block.id.slice(0, 8)}…</td>
                    <td className="py-2.5">{block.purchasedAt ? new Date(block.purchasedAt).toLocaleDateString() : "—"}</td>
                    <td className="py-2.5 font-semibold">{block.totalLicenses}</td>
                    <td className="py-2.5 text-muted-foreground text-xs">{block.billingRef || "—"}</td>
                    <td className="py-2.5 text-muted-foreground text-xs">{block.notes || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {/* Assigned Brokers */}
      <Card>
        <CardHeader className="pb-3 flex flex-row items-center justify-between">
          <CardTitle className="text-sm font-semibold text-muted-foreground">Assigned Brokers ({totalAssigned})</CardTitle>
          <div className="relative w-56">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
            <Input className="pl-8 h-8 text-xs" placeholder="Search brokers..."
              value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
          </div>
        </CardHeader>
        <CardContent>
          {brokerLinks.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <p className="text-sm">No brokers assigned yet</p>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-xs text-muted-foreground border-b border-border">
                <tr>
                  <th className="text-left pb-2">Agency</th>
                  <th className="text-left pb-2">Status</th>
                  <th className="text-left pb-2">Linked</th>
                  <th className="text-left pb-2">Funding</th>
                  <th className="pb-2"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {brokerLinks
                  .filter((l: any) => !searchQuery || (l.brokerAgencyName || "").toLowerCase().includes(searchQuery.toLowerCase()))
                  .map((link: any) => (
                    <tr key={link.id}>
                      <td className="py-2.5 font-medium">{link.brokerAgencyName || "—"}</td>
                      <td className="py-2.5">
                        <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${
                          link.status === "active"  ? "bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300" :
                          link.status === "pending" ? "bg-amber-100 dark:bg-amber-900 text-amber-700 dark:text-amber-300" :
                          "bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-300"
                        }`}>
                          {link.status === "pending" ? "Invited" : link.status === "active" ? "Active" : "Revoked"}
                        </span>
                      </td>
                      <td className="py-2.5 text-muted-foreground text-xs">
                        {link.linkedAt ? new Date(link.linkedAt).toLocaleDateString() : "—"}
                      </td>
                      <td className="py-2.5 text-xs text-muted-foreground">
                        {link.gaFunded ? "GA-funded" : "Self-funded"}
                      </td>
                      <td className="py-2.5 text-right">
                        {link.status !== "license_revoked" && (
                          <Button variant="ghost" size="sm"
                            className="h-7 text-xs text-red-500 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-950"
                            onClick={() => setRevokeTarget(link)}>
                            Revoke
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {/* ── Request Additional Licenses Dialog ─────────────────────────────────── */}
      <Dialog open={showRequestDialog} onOpenChange={(open) => { if (!open) setShowRequestDialog(false); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShoppingCart className="w-4 h-4 text-primary" />
              Request Additional Licenses
            </DialogTitle>
          </DialogHeader>

          {requestDone ? (
            /* ── Success state ── */
            <div className="py-6 text-center space-y-3">
              <div className="w-12 h-12 rounded-full bg-green-100 dark:bg-green-900 flex items-center justify-center mx-auto">
                <CheckCircle2 className="w-6 h-6 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <p className="font-semibold text-foreground">Request submitted</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Your request for <strong>{reqQuantity} license{reqQuantity > 1 ? "s" : ""}</strong> has been sent to your Administr sales contact.
                </p>
                <p className="text-sm text-muted-foreground mt-1">
                  You'll hear back within 1 business day.
                </p>
              </div>
              {requestResult?.requestId && (
                <p className="text-[11px] text-muted-foreground font-mono">Ref: {requestResult.requestId.slice(0, 8)}</p>
              )}
              <Button className="mt-2" onClick={() => setShowRequestDialog(false)}>Done</Button>
            </div>
          ) : (
            /* ── Request form ── */
            <>
              {/* Current inventory summary */}
              <div className="rounded-lg bg-muted border border-border px-3 py-2.5 grid grid-cols-3 gap-2 text-center">
                <div>
                  <p className="text-xs text-muted-foreground">Purchased</p>
                  <p className="text-lg font-bold text-foreground">{totalPurchased}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Assigned</p>
                  <p className="text-lg font-bold text-primary">{totalAssigned}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Available</p>
                  <p className={`text-lg font-bold ${lowOnLicenses ? "text-amber-500 dark:text-amber-400" : "text-green-600 dark:text-green-400"}`}>
                    {Math.max(0, totalAvailable)}
                  </p>
                </div>
              </div>

              <div className="space-y-4 py-1">
                {/* Quantity */}
                <div className="space-y-1.5">
                  <Label>Number of additional licenses needed <span className="text-red-500 dark:text-red-400">*</span></Label>
                  <Input
                    type="number"
                    min={1}
                    max={500}
                    value={reqQuantity}
                    onChange={(e) => setReqQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                    className="h-9"
                  />
                  <p className="text-[11px] text-muted-foreground">
                    After this request: {totalPurchased + reqQuantity} total · {totalAvailable + reqQuantity} available
                  </p>
                </div>

                {/* Timeline */}
                <div className="space-y-1.5">
                  <Label>When do you need them?</Label>
                  <Select value={reqTimeline} onValueChange={setReqTimeline}>
                    <SelectTrigger className="h-9">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="asap">As soon as possible</SelectItem>
                      <SelectItem value="within_30_days">Within 30 days</SelectItem>
                      <SelectItem value="within_60_days">Within 60 days</SelectItem>
                      <SelectItem value="planning_ahead">Planning ahead (60+ days)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Notes */}
                <div className="space-y-1.5">
                  <Label>Notes <span className="text-muted-foreground font-normal">(optional)</span></Label>
                  <Textarea
                    placeholder="e.g. Onboarding a new book of business, expanding into new region…"
                    rows={3}
                    className="resize-none text-sm"
                    value={reqNotes}
                    onChange={(e) => setReqNotes(e.target.value)}
                  />
                </div>

                {/* Contact info */}
                <div className="space-y-3 pt-1 border-t">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Best contact for follow-up</p>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label className="text-xs">Name</Label>
                      <Input
                        placeholder="Your name"
                        className="h-8 text-sm"
                        value={reqContactName}
                        onChange={(e) => setReqContactName(e.target.value)}
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs">Email</Label>
                      <Input
                        type="email"
                        placeholder="you@agency.com"
                        className="h-8 text-sm"
                        value={reqContactEmail}
                        onChange={(e) => setReqContactEmail(e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setShowRequestDialog(false)}>Cancel</Button>
                <Button
                  onClick={() => requestLicensesMutation.mutate({
                    quantity: reqQuantity,
                    timeline: TIMELINE_LABELS[reqTimeline] || reqTimeline,
                    notes: reqNotes,
                    contactName: reqContactName,
                    contactEmail: reqContactEmail,
                  })}
                  disabled={reqQuantity < 1 || requestLicensesMutation.isPending}
                >
                  {requestLicensesMutation.isPending ? "Submitting…" : "Submit Request"}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* ── Assign License Dialog ───────────────────────────────────────────────── */}
      <Dialog open={showAssignDialog} onOpenChange={setShowAssignDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assign License to Broker</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label>Agency Name</Label>
              <Input placeholder="e.g. Sunrise Benefits Group"
                value={brokerName} onChange={(e) => setBrokerName(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>Primary Contact Email</Label>
              <Input type="email" placeholder="contact@agency.com"
                value={brokerEmail} onChange={(e) => setBrokerEmail(e.target.value)} />
            </div>
            {blocks.length > 0 && (
              <div className="space-y-1.5">
                <Label>License Block</Label>
                <select
                  className="w-full h-9 rounded-md border border-input bg-background px-3 py-1 text-sm"
                  value={selectedBlock}
                  onChange={(e) => setSelectedBlock(e.target.value)}
                >
                  <option value="">Select a block…</option>
                  {blocks.map((b: any) => (
                    <option key={b.id} value={b.id}>
                      Block {b.id.slice(0, 8)} · {b.totalLicenses} seats · purchased {b.purchasedAt ? new Date(b.purchasedAt).toLocaleDateString() : ""}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <p className="text-xs text-muted-foreground">
              An invite email will be sent to the broker contact. A license seat will be reserved immediately.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAssignDialog(false)}>Cancel</Button>
            <Button
              onClick={() => assignMutation.mutate({ brokerAgencyName: brokerName, brokerContactEmail: brokerEmail, licenseBlockId: selectedBlock || undefined })}
              disabled={!brokerName || !brokerEmail || assignMutation.isPending}
            >
              {assignMutation.isPending ? "Sending…" : "Send Invite"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Revoke Confirmation ─────────────────────────────────────────────────── */}
      <AlertDialog open={!!revokeTarget} onOpenChange={(open) => !open && setRevokeTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Revoke License</AlertDialogTitle>
            <AlertDialogDescription>
              This will revoke the license for <strong>{revokeTarget?.brokerAgencyName}</strong>. Their access to the platform will be affected. This action can only be undone by Administr admin action.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              onClick={() => revokeTarget && revokeMutation.mutate(revokeTarget.id)}
            >
              Revoke License
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
